import React from 'react';
import { withRouter } from 'react-router';
import oh from 'output-helpers';
import { FaEllipsisV } from 'react-icons/fa';
import styled, { keyframes } from 'styled-components';
import withStore from 'with-store';
import userInputs from 'user-inputs';

import Navbar from '../base/Navbar';
import TaskCard from '../base/TaskCard';
import LocalSpinner from '../base/LocalSpinner';
import Chat from '../base/Chat';
import InformationCard from '../base/InformationCard';
import api_helpers from '../../helpers/api_helpers';
import * as task_helpers from '../../helpers/task_helpers';
import * as profile_helpers from '../../helpers/profile_helpers';
import ItemOptionDropdown from '../base/ItemOptionDropdown';
import Notifications from '../../notifications';

const OrderCategories = {
    INFORMATION: 0,
    TASKS: 1,
    MESSAGES: 2,
};

const OrderState = {
    PUBLISHED: 'published',
    ASSIGNED: 'assigned',
    COMPLETED: 'completed',
    APPROVED: 'approved',
};

class OrderView extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            selected_category: OrderCategories.INFORMATION,
        };

        const anim_slide_in = keyframes`
            from {
                transform: translateX(-100%);
            }
            to {
                transform: none;
            }
        `;

        const anim_overlay = keyframes`
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        `;

        this.container = styled.div`
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 10;
            pointer-events: none;
            span.link {
                margin: 2px;
            }
            .order_overlay {
                pointer-events: all;
                animation: ${anim_overlay} 0.5s both;
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                background-color: rgba(0, 0, 0, 0.2);
            }
            .order_view {
                pointer-events: all;
                animation: ${anim_slide_in} 0.6s 0.3s both;
                width: 60%;
                background-color: ${props => props.theme.colors.background};
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
            }
            .order_action {
                margin: 0px 4px 0 12px;
                display: inline-block;
                vertical-align: middle;
                &:hover {
                    svg {
                        opacity: 1;
                    }
                }
                svg {
                    height: 24px;
                    vertical-align: middle;
                    opacity: 0.7;
                    fill: #fff;
                }
            }
            .order_body {
                height: calc(
                    100% - ${p => p.theme.sizes.sub_navbar.height * 2}px
                );
                padding: 20px 30px;
                overflow: auto;
                & > div {
                    vertical-align: top;
                }
                &.no_padding {
                    height: 100%;
                    padding: 0;
                }
            }
            .task_card_container {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(280px, 300px));
            }
        `;

        this.order_status_flag = styled.div`
            animation: ${anim_overlay} 0.4s 0.5s both;
            position: absolute;
            top: 20px;
            right: 68px;
            padding: 20px;
            border-radius: 8px;
            background-color: ${props =>
                props.theme.colors.states[props.state]
                    ? props.theme.colors.states[props.state]
                    : 'gray'};
            text-align: center;
            z-index: 8;
            p {
                font-size: 24px;
                font-weight: bold;
                display: inline-block;
                vertical-align: middle;
            }
        `;
    }

    componentDidMount() {
        let closePromptAndWarn = (event, cb) => {
            if (this.props.userPrompt.isOpen()) {
                let prompt_tag = this.props.userPrompt.getTag();
                if (prompt_tag !== 'order') {
                    return;
                }

                let selected_order = this.props.se.getSelectedOrder();
                if (selected_order === null) {
                    return;
                }

                if (cb(selected_order) === true) {
                    this.props.userPrompt.cancel();
                    this.props.userPrompt.alert({
                        props: {
                            title: oh.translate('concurrent_modification'),
                            message: oh.translate(
                                'concurrent_modification_desc',
                            ),
                        },
                    });
                }
            }
        };

        let closePromptAndWarnOrder = event => {
            closePromptAndWarn(event, selected_order => {
                if (selected_order.id === event.data.id) {
                    return true;
                }
                return false;
            });
        };
        let closePromptAndWarnTask = event => {
            closePromptAndWarn(event, selected_order => {
                let problem = false;
                if (/^tasks_/.test(event.type)) {
                    problem = event.data.some(
                        task => task.order_id === selected_order.id,
                    );
                } else {
                    problem = event.data.id === selected_order.id;
                }
                return problem;
            });
        };

        Notifications.addEventInspector(this, 'order_updated', event => {
            closePromptAndWarnOrder(event);
        });
        Notifications.addEventInspector(this, 'order_deleted', event => {
            closePromptAndWarnOrder(event);
        });
        Notifications.addEventInspector(this, 'task_updated', event => {
            closePromptAndWarnTask(event);
        });
        Notifications.addEventInspector(this, 'tasks_updated', event => {
            closePromptAndWarnTask(event);
        });
        Notifications.addEventInspector(this, 'task_deleted', event => {
            closePromptAndWarnTask(event);
        });
        Notifications.addEventInspector(this, 'tasks_deleted', event => {
            closePromptAndWarnTask(event);
        });
    }

    componentWillUnmount() {
        Notifications.clearEventInspectors(this);
    }

    renderEntityLinks(tasks) {
        let entity_links = [];
        if (tasks.length > 0) {
            tasks[0].entities.forEach((entity, entity_index) => {
                entity_links.push(
                    <span
                        key={entity_index}
                        className="link"
                        onClick={() => {
                            this.props.se.setSelectedEntity(entity.ref);
                        }}
                    >
                        {entity.id}
                    </span>,
                );
            });
        }
        return entity_links;
    }

    renderOrderBody(this_order, tasks) {
        if (this.state.selected_category === OrderCategories.INFORMATION) {
            const information_items = [
                {
                    label: oh.translate('invoice_info'),
                    data: this_order.data.invoice_info,
                    free_text: true,
                    onClick: () => {
                        if (this_order.state === OrderState.APPROVED) {
                            return;
                        }

                        this.props.userPrompt.confirm(
                            {
                                props: {
                                    title: oh.translate('invoice_info'),
                                },
                                inputs: [
                                    {
                                        type: 'textarea',
                                        key: 'info',
                                        default_value:
                                            this_order.data.invoice_info,
                                    },
                                ],
                            },
                            values => {
                                withStore.actions.spinner.add();
                                Promise.resolve()
                                    .then(() => {
                                        let existing_order = this.props.store
                                            .order.by_id[this_order.id];
                                        let altered_order = Object.assign(
                                            {},
                                            existing_order,
                                        );
                                        altered_order.data = Object.assign(
                                            {},
                                            altered_order.data,
                                        );
                                        altered_order.data.invoice_info =
                                            values.info;
                                        return api_helpers
                                            .updateOrder(altered_order)
                                            .then(updated_order => {
                                                withStore.actions.order.setOrder(
                                                    updated_order,
                                                );
                                            });
                                    })
                                    .finally(() => {
                                        withStore.actions.spinner.remove();
                                    });
                            },
                        );
                        this.props.userPrompt.setTag('order');
                    },
                },
                {
                    label: oh.translate('tags'),
                    data: tasks
                        .map(task => task.tags)
                        .flat(2)
                        .map(tag => tag.name)
                        .toString(),
                },
                {
                    label: oh.translate('created_date'),
                    data: this_order.display_date,
                },
                {
                    label: oh.translate('created_by'),
                    data: this_order.creator.full_name || '',
                },
                {
                    label: oh.translate('associated_users'),
                    data: (this_order.getAssociatedUsers() || [])
                        .map(x => x.full_name)
                        .join(', '),
                },
                {
                    label: oh.translate('associated_orgs'),
                    data: (this_order.getAssociatedOrgs() || [])
                        .map(x => x.name)
                        .join(', '),
                },
                {
                    label: oh.translate('assignee_org'),
                    data: this_order.assignee_org
                        ? this_order.assignee_org.name
                        : '',
                },
                {
                    label: oh.translate('address'),
                    data: this_order.data.address
                        ? this_order.data.address
                        : '',
                },
                {
                    label: oh.translate('entities'),
                    data: this.renderEntityLinks(tasks),
                },
            ];
            return (
                <div className="order_body">
                    <InformationCard
                        title={oh.translate('information')}
                        items={information_items}
                    />
                </div>
            );
        } else if (this.state.selected_category === OrderCategories.TASKS) {
            return (
                <div className="order_body">
                    <div className="task_card_container">
                        {tasks.map((item, i) => {
                            return (
                                <TaskCard
                                    entities={item.entities}
                                    key={i}
                                    task={item}
                                />
                            );
                        })}
                    </div>
                </div>
            );
        } else if (this.state.selected_category === OrderCategories.MESSAGES) {
            return (
                <div className="order_body no_padding">
                    <Chat
                        ref_id={
                            'order://' + this.props.se.getSelectedOrder().id
                        }
                    />
                </div>
            );
        }
    }

    adminCompleteOrder(order, desc) {
        withStore.actions.spinner.add();
        let now = new Date();
        Promise.resolve()
            .then(() => {
                if (desc.length > 0) {
                    return api_helpers
                        .postMessage({
                            type: 'text',
                            ref: 'order://' + order.id,
                            data: {
                                body: desc,
                            },
                        })
                        .then(msg => {
                            this.props.actions.messages.setMessage(msg);
                            profile_helpers.setLastViewed(
                                order.id,
                                'order_messages',
                            );
                        });
                }
                return null;
            })
            .then(() => {
                let tasks = withStore
                    .getState()
                    .tasks.all.filter(task => task.order_id === order.id)
                    .filter(task => typeof task.completed !== 'boolean')
                    .map(task => {
                        return Object.assign({}, task, { completed: false });
                    });

                if (tasks.length > 0) {
                    return api_helpers
                        .updateTasks(tasks)
                        .then(updated_tasks => {
                            this.props.actions.tasks.setTasks(updated_tasks);
                        });
                }
                return null;
            })
            .then(() => {
                let existing_order = withStore.getState().order.by_id[order.id];
                let altered_order = Object.assign({}, existing_order);
                altered_order.state = OrderState.COMPLETED;
                altered_order.data = Object.assign({}, existing_order.data);
                altered_order.data.last_message_posted_at = now.toISOString();

                return api_helpers
                    .updateOrder(altered_order)
                    .then(updated_order => {
                        withStore.actions.order.setOrder(updated_order);
                    });
            })
            .finally(() => {
                withStore.actions.spinner.remove();
            });
    }

    adminRejectOrder(order, desc) {
        withStore.actions.spinner.add();
        let now = new Date();
        Promise.resolve()
            .then(() => {
                if (desc.length > 0) {
                    return api_helpers
                        .postMessage({
                            type: 'text',
                            ref: 'order://' + order.id,
                            data: {
                                body: desc,
                            },
                        })
                        .then(msg => {
                            this.props.actions.messages.setMessage(msg);
                            profile_helpers.setLastViewed(
                                order.id,
                                'order_messages',
                            );
                        });
                }
                return null;
            })
            .then(() => {
                let existing_order = withStore.getState().order.by_id[order.id];
                let altered_order = Object.assign({}, existing_order);
                altered_order.state = OrderState.ASSIGNED;
                altered_order.data = Object.assign({}, existing_order.data);
                altered_order.data.last_message_posted_at = now.toISOString();
                return api_helpers
                    .updateOrder(altered_order)
                    .then(updated_order => {
                        withStore.actions.order.setOrder(updated_order);
                    });
            })
            .finally(() => {
                withStore.actions.spinner.remove();
            });
    }

    adminApproveOrder(order) {
        withStore.actions.spinner.add();
        Promise.resolve()
            .then(() => {
                let altered_order = Object.assign(
                    {},
                    withStore.getState().order.by_id[order.id],
                );
                altered_order.state = OrderState.APPROVED;
                return api_helpers
                    .updateOrder(altered_order)
                    .then(updated_order => {
                        withStore.actions.order.setOrder(updated_order);
                    });
            })
            .finally(() => {
                withStore.actions.spinner.remove();
            });
    }

    orderOptions(order) {
        let opts = [];

        if (order.state === OrderState.ASSIGNED) {
            opts.push({
                label: oh.translate('order_state_change_complete'),
                onClick: () => {
                    this.props.userPrompt.confirm(
                        {
                            props: {
                                title: oh.translate('are_you_sure'),
                                message: oh.translate(
                                    'describe_why_admin_order_complete',
                                ),
                                confirm_button_label: oh.translate('yes'),
                                cancel_button_label: oh.translate('no'),
                            },
                            inputs: [
                                {
                                    type: 'textarea',
                                    key: 'desc',
                                    default_value: '',
                                },
                            ],
                        },
                        values => {
                            return this.adminCompleteOrder(
                                order,
                                values.desc.trim(),
                            );
                        },
                    );
                    this.props.userPrompt.setTag('order');
                },
            });
        } else if (order.state === OrderState.COMPLETED) {
            opts.push({
                label: oh.translate('order_state_change_reject'),
                onClick: () => {
                    this.props.userPrompt.confirm(
                        {
                            props: {
                                title: oh.translate('are_you_sure'),
                                message: oh.translate(
                                    'describe_why_admin_order_reject',
                                ),
                                confirm_button_label: oh.translate('yes'),
                                cancel_button_label: oh.translate('no'),
                            },
                            inputs: [
                                {
                                    type: 'textarea',
                                    key: 'desc',
                                    default_value: '',
                                },
                            ],
                        },
                        values => {
                            return this.adminRejectOrder(
                                order,
                                values.desc.trim(),
                            );
                        },
                    );
                    this.props.userPrompt.setTag('order');
                },
            });
            opts.push({
                label: oh.translate('order_state_change_approve'),
                onClick: () => {
                    this.props.userPrompt.confirm(
                        {
                            props: {
                                title: oh.translate('are_you_sure'),
                                confirm_button_label: oh.translate('yes'),
                                cancel_button_label: oh.translate('no'),
                            },
                        },
                        values => {
                            return this.adminApproveOrder(order);
                        },
                    );
                    this.props.userPrompt.setTag('order');
                },
            });
        }

        if (order.state !== OrderState.APPROVED) {
            opts.push({
                label: oh.translate('remove'),
                onClick: () => {
                    this.props.userPrompt.confirm(
                        {
                            props: {
                                title: oh.translate('are_you_sure'),
                                message: oh.translate('order_removal_long'),
                                confirm_button_label: oh.translate('yes'),
                                cancel_button_label: oh.translate('no'),
                            },
                        },
                        values => {
                            let tasks = withStore
                                .getState()
                                .tasks.all.filter(
                                    task => task.order_id === order.id,
                                );

                            withStore.actions.spinner.add();
                            Promise.resolve()
                                .then(() => {
                                    if (tasks.length > 0) {
                                        return api_helpers
                                            .deleteTasks(tasks)
                                            .then(deleted_tasks => {
                                                withStore.actions.tasks.deleteTasks(
                                                    deleted_tasks,
                                                );
                                                return deleted_tasks;
                                            });
                                    }
                                    return null;
                                })
                                .then(() => {
                                    return api_helpers
                                        .deleteOrder(order)
                                        .then(deleted_order => {
                                            withStore.actions.order.deleteOrder(
                                                deleted_order,
                                            );
                                            return null;
                                        });
                                })
                                .finally(() => {
                                    withStore.actions.spinner.remove();
                                });
                        },
                    );
                    this.props.userPrompt.setTag('order');
                },
            });
        }

        return opts;
    }

    renderContextMenu(order) {
        if (order.state === OrderState.APPROVED) {
            return null;
        }

        return (
            <ItemOptionDropdown options={this.orderOptions(order)}>
                <FaEllipsisV />
            </ItemOptionDropdown>
        );
    }

    triggerCloseView() {
        this.props.se.setSelectedOrder(null);
        this.setState({ selected_category: OrderCategories.INFORMATION });
    }

    render() {
        const this_order = this.props.se.getSelectedOrder();
        let is_entity_open = this.props.se.getSelectedEntity() !== null;
        if (this_order === null) {
            return null;
        }

        let tasks = this.props.se.getTasksFromOrderId(this_order.id);

        if (tasks === null) {
            return (
                <this.container>
                    {is_entity_open ? null : (
                        <div
                            className="order_overlay"
                            onClick={() => {
                                this.props.se.setSelectedOrder(null);
                            }}
                        />
                    )}
                    <div className="order_view">
                        <LocalSpinner />
                    </div>
                </this.container>
            );
        }

        tasks = task_helpers.groupAndConvertTasksToTaskCardFormat(tasks);
        tasks = Object.keys(tasks).map(key => tasks[key]);

        let message_tab_classes = [];
        let last_viewed = this.props.se.getLastViewed(
            this_order.id,
            'order_messages',
        );
        if (
            this_order.data.hasOwnProperty('last_message_posted_at') &&
            this_order.data.last_message_posted_at > last_viewed
        ) {
            message_tab_classes.push('updated');
        }
        let messages_last_viewed = this.props.se.getLastViewed(
            this_order.id,
            'order_messages',
        );
        let new_message = false;
        if (
            (this_order.data.hasOwnProperty(
                'last_message_posted_at',
                ) &&
                this_order.data
                    .last_message_posted_at >
                messages_last_viewed) ||
            (messages_last_viewed === null &&
                this_order.data
                    .last_message_posted_at !==
                undefined)
        ) {
            new_message = true;
        }

        const categories = [
            {
                name: 'information',
                onClick: () => {
                    this.setState({
                        selected_category: OrderCategories.INFORMATION,
                    });
                },
            },
            {
                name: 'tasks',
                onClick: () => {
                    this.setState({ selected_category: OrderCategories.TASKS });
                },
            },
            {
                name: 'messages',
                classes: message_tab_classes,
                messages_count:  new_message,
                onClick: () => {
                    this.setState({
                        selected_category: OrderCategories.MESSAGES,
                    });
                    profile_helpers.setLastViewed(
                        this_order.id,
                        'order_messages',
                    );
                },
            },
        ];
        return (
            <this.container>
                {is_entity_open ? null : (
                    <div
                        className="order_overlay"
                        onClick={() => {
                            this.triggerCloseView();
                        }}
                    />
                )}
                <div className="order_view">
                    <Navbar
                        bigTitle={'#' + this_order.id}
                        icon={'order'}
                        close={() => {
                            this.triggerCloseView();
                        }}
                    >
                        <this.order_status_flag state={this_order.state}>
                            <p>
                                {oh.translate('singular_' + this_order.state)}
                            </p>
                            <div className="order_action">
                                {this.renderContextMenu(this_order)}
                            </div>
                        </this.order_status_flag>
                    </Navbar>
                    <Navbar
                        categories_with_message_count={categories}
                        border
                        selected_category={this.state.selected_category}
                    />
                    {this.renderOrderBody(this_order, tasks)}
                </div>
            </this.container>
        );
    }
}

export default withRouter(userInputs(withStore(OrderView)));



// WEBPACK FOOTER //
// ./src/components/views/OrderView.jsx